import Session from 'Faster/model/Session';
import SessionJsonTransformer from 'Faster/serialization/SessionJsonTransformer';
import StorageEngine from './StorageEngine';

class SessionStorage {
  private static readonly LOCAL_SESSION_STORAGE_KEY: string = 'fstr.session';
  private static readonly LOCAL_CHECKPOINT_STORAGE_KEY: string = 'fstr.checkpoint';
  private static readonly LOCAL_NTP_CHECKPOINT_STORAGE_KEY: string = 'fstr.ntp_checkpoint';
  private _session?: Session = null;
  private _checkpoint?: Date;
  private _ntpCheckpoint?: Date;
  private storage: StorageEngine;

  public constructor(storageEngine: StorageEngine, externalSessionId?: string) {
    this.storage = storageEngine;
    if (typeof externalSessionId === 'string' && externalSessionId.trim().length > 0) {
      this._session = new Session(externalSessionId);
    } else {
      this._session = this.retrieveSession();
    }

    const [checkpoint, ntpTimestamp] = this.retrieveCheckpoint();
    this._checkpoint = checkpoint;
    this._ntpCheckpoint = ntpTimestamp;
  }

  public set session(session: Session) {
    this._session = session;

    this.storeSession(session);
  }

  public get session(): Session | undefined {
    return this._session;
  }

  public set checkpoint(date: Date) {
    this._checkpoint = date;
    this.storage.store(SessionStorage.LOCAL_CHECKPOINT_STORAGE_KEY, date.toISOString());
  }

  public get checkpoint(): Date | undefined {
    return this._checkpoint;
  }

  public set ntpCheckpoint(date: Date) {
    this._ntpCheckpoint = date;
    this.storage.store(SessionStorage.LOCAL_NTP_CHECKPOINT_STORAGE_KEY, date.toISOString());
  }

  public get ntpCheckpoint(): Date | undefined {
    return this._ntpCheckpoint;
  }

  private storeSession(session: Session): void {
    this.storage.store(SessionStorage.LOCAL_SESSION_STORAGE_KEY, SessionJsonTransformer.toJson(session));
  }

  private retrieveSession(): Session | undefined {
    const retrievedInfo = this.storage.retrieve(SessionStorage.LOCAL_SESSION_STORAGE_KEY);
    try {
      return SessionJsonTransformer.fromJson(retrievedInfo);
    } catch (error) {
      console.warn('Retrieved invalid json from storage:', retrievedInfo);
    }
    return null;
  }

  private retrieveCheckpoint(): [Date | undefined, Date | undefined] {
    const checkpoint = this.storage.retrieve(SessionStorage.LOCAL_CHECKPOINT_STORAGE_KEY) || null;
    const ntpCheckpoint = this.storage.retrieve(SessionStorage.LOCAL_NTP_CHECKPOINT_STORAGE_KEY) || null;

    return [(checkpoint && new Date(checkpoint)) || null, (ntpCheckpoint && new Date(ntpCheckpoint)) || null];
  }
}

export default SessionStorage;
